/**
 * Die EINE Regel „wo Google-Daten auf dem Schirm stehen, steht Google dran".
 *
 * Bauplan: docs/KONZEPT-RECHERCHE-SICHTBARKEIT.md § 6.2 („Attributionspflicht: Wer Google-Daten
 * anzeigt, muss Google kenntlich machen; bei Reviews zusaetzlich immer den Autor"), Kriterium
 * R4.5 in docs/LOOP-RECHERCHE.md. Quelle der Auflage: Google Places Policies
 * (https://developers.google.com/maps/documentation/places/web-service/policies) und
 * Maps Platform Terms (https://cloud.google.com/maps-platform/terms).
 *
 * ── Das ist keine Kosmetik, sondern eine Nutzungsbedingung ────────────────────────────────────
 * Die Policies verlangen zweierlei, und beides ist eine Bedingung dafuer, die Daten ueberhaupt
 * anzeigen zu duerfen:
 *
 *  1. **Herkunft sichtbar:** Wird Places-Inhalt auf einer Flaeche gezeigt, die NICHT zugleich eine
 *     Google-Karte enthaelt, muss das Google-Logo dabeistehen; wo dafuer kein Platz ist, tritt der
 *     Klartext an seine Stelle. MAESTRO zeigt Places-Inhalte ausschliesslich ohne Karte (Kunden-
 *     Profil, Modul-Kachel) — die Pflicht greift also immer, wenn ueberhaupt etwas angezeigt wird.
 *  2. **Attribution unveraendert:** Sie darf nicht entfernt, gekuerzt oder umformuliert werden.
 *     Deshalb steht der Text hier als EINE Konstante, und `googleAttribution()` gibt ein
 *     eingefrorenes Objekt zurueck: `attribution.text = "…"` wirft im strikten Modus (ESM), statt
 *     leise eine gekuerzte Variante auf den Schirm zu lassen.
 *
 * ── Warum Klartext und nicht das Logo ─────────────────────────────────────────────────────────
 * Bewusst der Klartext-Weg, aus einem Grund, der fuer die Auflage zaehlt: Ein selbst nachgebautes
 * Logo (SVG-Pfad von Hand) waere ein VERAENDERTES Logo — genau das, was die Policies untersagen.
 * Ein Remote-Bild von Googles Servern waere eine externe Abhaengigkeit in einer Backoffice-Maske.
 * Der Klartext ist die vorgesehene Ausweichform bei Platzmangel, und die Herkunftszeile, in der er
 * steht, ist mit ~11 px genau so ein Platz. Wenn das Logo als Asset ins Repo kommt, ersetzt es
 * ausschliesslich die Darstellung in `apps/web/src/ui/GoogleAttribution.tsx` — die Regel hier
 * bleibt dieselbe.
 *
 * ── Warum ein eigenes Paket und kein `if` in der Komponente ───────────────────────────────────
 * `apps/web` hat KEINEN Test-Runner (Befund aus R0.2, seither in R0.7/R0.9/R1.6 bestaetigt). Eine
 * Regel wie „nicht abschaltbar" ist in einer React-Komponente unpruefbar — und genau dort
 * verschwindet sie: jemand legt die Zeile hinter ein Flag, weil sie im Screenshot stoert, und
 * niemand merkt es. Dieselbe Bauform wie `@maestro/recherche-stand` (R1.6),
 * `@maestro/anreicherung` (R0.7), `@maestro/firmenprofil` (R0.9): reine Funktion im Paket, Tests
 * in `apps/api/test/google-attribution.test.ts` (jene Suite laeuft garantiert in der CI). Dieses
 * Paket hat deshalb bewusst KEIN `test`-Skript — `apps/api/test/ci-testabdeckung.test.ts` verlangt
 * fuer jedes Paket MIT `test`-Skript einen eigenen CI-Aufruf.
 *
 * ── Was hier NICHT hingehoert ────────────────────────────────────────────────────────────────
 * Der Stand („Google Places · Stand 12.06.2026") — das ist `@maestro/recherche-stand` (R1.6). Die
 * beiden Zeilen stehen nebeneinander und beantworten verschiedene Fragen: der Stand sagt, WIE ALT
 * die Angabe ist, die Attribution, WEM sie gehoert.
 */

/* ────────────────────────────── Der Text ───────────────────────────────────────────────────── */

/**
 * Was neben Google-Angaben steht. Genau diese Zeichenfolge, unveraendert.
 *
 * „Google Maps" und nicht „Google": Places ist Teil der Maps Platform, und die Policies nennen den
 * Produktnamen. Wer hier kuerzt, aendert die Attribution.
 */
export const GOOGLE_ATTRIBUTION = "Google Maps";

/** Der Langtext fuer `title`/`aria-label` — erklaert die Zeile, ersetzt sie aber nie. */
export const GOOGLE_ATTRIBUTION_HINWEIS = "Diese Angaben stammen von Google Maps.";

/**
 * Woran die Ratsche erkennt, dass eine Flaeche die Attribution ausgibt. Steht hier und nicht im
 * Test, weil es zur REGEL gehoert (dieselbe Ueberlegung wie `STAND_AUSGABEN` in
 * `@maestro/recherche-stand`): wer eine neue Google-Flaeche baut, sieht hier, welchen Namen sie
 * tragen muss.
 *
 *  - `googleAttribution` — diese Regel, direkt aufgerufen.
 *  - `GoogleAttribution` — die Komponente in `apps/web/src/ui/GoogleAttribution.tsx`, die sie fuellt.
 */
export const ATTRIBUTIONS_AUSGABEN = ["googleAttribution", "GoogleAttribution"] as const;

/**
 * Die Feldnamen, unter denen Google-Places-Inhalte in der Oberflaeche ankommen — die Prueflisten
 * der Ratsche haengen daran (jede Quelltext-Einheit in `apps/web/src`, die eines dieser Felder
 * liest, muss die Attribution tragen oder mit Begruendung ausgenommen sein).
 *
 * Die Liste ist gegen Drift abgesichert: `apps/api/test/google-attribution.test.ts` liest die
 * Nutzlast `GooglePlacesLookup` aus `apps/web/src/ui/types.ts` und verlangt, dass JEDES Feld
 * daraus entweder hier steht oder in `google-attribution-ausnahmen.ts` als reines Verwaltungsfeld
 * ausgewiesen ist. Ein neues Google-Feld erzwingt damit eine Entscheidung, kein Schweigen.
 *
 * `placeId`/`googlePlaceId` stehen bewusst DRIN: die Place-ID ist der einzige Google-Wert, den wir
 * dauerhaft halten duerfen (§ 6.2/P-6) — sie ist damit erst recht eine Google-Angabe, sobald sie
 * jemand zu sehen bekommt.
 */
export const GOOGLE_ANGABEN_FELDER = [
  "placeId",
  "googlePlaceId",
  /**
   * R3.3: die Trefferliste bei Mehrdeutigkeit. Sie ist die dichteste Google-Angabe, die MAESTRO
   * anzeigt — drei Namen, drei Anschriften, drei Telefonnummern auf einmal. Genau dort, wo jemand
   * eine Auswahl trifft, muss stehen, wessen Angaben er da vergleicht.
   */
  "candidates",
  "suggestedName",
  "suggestedFormattedAddress",
  "suggestedPhone",
  "rating",
  "userRatingsTotal",
  "businessStatus",
  "openingHours",
  "openingHoursPending",
  "weekdayDescriptions",
] as const;

/* ────────────────────────────── Die Regel ──────────────────────────────────────────────────── */

/** Was auf einer Flaeche aus Google stammt. */
export interface AttributionsLage {
  /**
   * Die Werte, die diese Flaeche aus einem Google-Abruf anzeigt — in der Reihenfolge egal, es
   * zaehlt nur, ob ueberhaupt einer davon einen Inhalt hat.
   *
   * ABSICHTLICH die Werte selbst und kein `sichtbar: boolean`: ein Flag koennte jemand auf `false`
   * setzen und die Werte trotzdem rendern. Die Daten sind der Schalter — wer die Attribution
   * loswerden will, muss die Angaben loswerden. Das ist der Kern von „nicht abschaltbar".
   */
  googleWerte: readonly unknown[];
  /**
   * Steht auf derselben Flaeche eine eingebettete Google-Karte? Dann traegt die Karte das Logo
   * bereits, und eine zweite Attribution waere Doppelung. Heute gibt es in MAESTRO keine
   * eingebettete Karte — das Feld ist der dokumentierte Ausnahmefall der Policies, nicht ein
   * Schalter fuer den Alltag.
   */
  googleKarteSichtbar?: boolean;
}

export interface GoogleAttributionsAnzeige {
  /** Der Text, der auf dem Schirm steht. Immer `GOOGLE_ATTRIBUTION`, nie gekuerzt. */
  readonly text: string;
  /** Erklaerung fuer `title`/`aria-label`. */
  readonly hinweis: string;
}

/**
 * Hat diese Flaeche ueberhaupt eine Google-Angabe auf dem Schirm?
 *
 * Leer heisst: `null`, `undefined`, leerer/nur-Leerzeichen-String, leeres Array (rekursiv). Die
 * Zahl `0` und `false` sind Werte, keine Leere — eine Bewertung von 0 waere eine Angabe.
 *
 * Diese Funktion ist zugleich die Antwort auf R2.7 (Google-Rohdaten verfallen nach Frist): nach
 * dem Verfall steht in der Zeile nur noch die `place_id`, die Karte zeigt einen Verfallshinweis
 * und sonst nichts — dann gibt es nichts zu attribuieren, und die Zeile bleibt weg, statt an einer
 * leeren Karte zu kleben.
 */
export function zeigtGoogleAngaben(werte: readonly unknown[]): boolean {
  return werte.some(vorhanden);
}

function vorhanden(wert: unknown): boolean {
  if (wert === null || wert === undefined) return false;
  if (typeof wert === "string") return wert.trim() !== "";
  if (Array.isArray(wert)) return wert.some(vorhanden);
  return true;
}

/**
 * Die Attribution zu einer Flaeche — oder `null`, wenn dort nichts von Google steht.
 *
 * Es gibt genau ZWEI Wege zu `null`, beide oben begruendet: keine Angabe sichtbar, oder eine
 * Google-Karte traegt das Logo schon. Einen dritten (ein „aus"-Flag, eine Rolle, eine
 * Einstellung) gibt es nicht und darf es nicht geben — die Ratsche in
 * `apps/api/test/google-attribution.test.ts` prueft beide Richtungen.
 *
 * Das Ergebnis ist eingefroren: `attribution.text = "G"` wirft im strikten Modus. Die Policies
 * verlangen eine UNVERAENDERTE Attribution; ein stilles Ueberschreiben waere genau der Verstoss,
 * den niemand im Diff sieht.
 */
export function googleAttribution(lage: AttributionsLage): GoogleAttributionsAnzeige | null {
  if (lage.googleKarteSichtbar === true) return null;
  if (!zeigtGoogleAngaben(lage.googleWerte)) return null;
  return Object.freeze({ text: GOOGLE_ATTRIBUTION, hinweis: GOOGLE_ATTRIBUTION_HINWEIS });
}

/* ────────────────────────────── Bewertungen: immer der Autor ───────────────────────────────── */

/**
 * Eine Google-Bewertung, so wie `authorAttribution` sie liefert.
 *
 * MAESTRO holt heute KEINE Bewertungstexte — die Feldmaske ist dagegen verriegelt (R2.7,
 * `apps/api/test/places-rohdaten-ttl.test.ts`). Die Regel steht hier trotzdem, weil sie an dem Tag
 * gebraucht wird, an dem jemand Bewertungen anzeigen will (Bewertungs-Widget W3), und weil eine
 * Regel, die erst mit dem Bedarf entsteht, im Zweifel zu spaet kommt.
 */
export interface GoogleBewertung {
  text?: string | null;
  /** `authorAttribution.displayName` — ohne ihn darf die Bewertung nicht angezeigt werden. */
  autorName?: string | null;
  /** `authorAttribution.uri` — der Profil-Link, wenn Google einen liefert. */
  autorProfilUrl?: string | null;
  /** `authorAttribution.photoUri` — das Autorenbild, wenn Google eins liefert. */
  autorFotoUrl?: string | null;
}

/** Der Versuch, eine Bewertung ohne ihren Autor anzuzeigen. */
export class BewertungOhneAutorFehler extends Error {
  constructor() {
    super(
      "Eine Google-Bewertung darf nicht ohne ihren Autor angezeigt werden (authorAttribution). " +
        "Entweder den Autor mitfuehren oder die Bewertung nicht anzeigen.",
    );
    this.name = "BewertungOhneAutorFehler";
  }
}

export interface BewertungsAttribution {
  readonly autor: string;
  readonly profilUrl: string | null;
  readonly fotoUrl: string | null;
}

/**
 * Die Autorenangabe zu einer Bewertung — oder ein Fehler.
 *
 * ABSICHTLICH ein Fehler und kein `null`: Bei der Herkunftszeile ist „ohne Datum" eine ehrliche
 * Aussage (R1.6). Hier gibt es kein ehrliches Weglassen — eine Bewertung ohne Autor DARF nicht auf
 * dem Schirm stehen. Wer sie trotzdem anzeigen will, muss den Fehler wegprogrammieren und steht
 * damit im Diff.
 */
export function bewertungsAttribution(bewertung: GoogleBewertung): BewertungsAttribution {
  const autor = bewertung.autorName?.trim();
  if (!autor) throw new BewertungOhneAutorFehler();
  return Object.freeze({
    autor,
    profilUrl: bewertung.autorProfilUrl?.trim() || null,
    fotoUrl: bewertung.autorFotoUrl?.trim() || null,
  });
}
